import { buildItemBookingChangedActivityEntry } from '../utils/buildItemBookingChangedActivityEntry'
import { useTripListBookingUpdate, type UpdateTripListBookingParams } from '../hooks/useTripListBookingUpdate'
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ComponentProps, type ComponentType } from 'react'
import { defineMessage, useIntl } from 'react-intl'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'

import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import {
  isLeFlightSavedItemData,
  type TransportOption,
  type TripStop,
} from 'tripPlanner/api/types'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useEventSubscription, useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { getStopCoordinates } from 'tripPlanner/utils/chatItinerary'
import { useTripListPricingContext } from '../contexts/TripListPricingContext'
import { useFetchCartPanelOffers } from '../hooks/useFetchCartPanelOffers'
import { useCartProjection } from '../contexts/CartProjectionContext'
import { CartDisplaySnapshotRegistration } from '../contexts/CartDisplaySnapshotRegistration'

import { buildPlaceDrawerOpenedActivityEntry } from './buildPlaceDrawerOpenedActivityEntry'
import { buildItemDeletedActivityEntry } from '../utils/buildItemDeletedActivityEntry'
import { buildItemsBulkDeletedActivityEntry } from '../utils/buildItemsBulkDeletedActivityEntry'
import { buildListFilterChangedActivityEntry } from './buildListFilterChangedActivityEntry'
import { buildThingsToDoOpenedActivityEntry } from '../utils/buildThingsToDoOpenedActivityEntry'
import { opensOnThingsToDo } from '../components/ExperienceListingDrawer/opensOnThingsToDo'
import { getCartItemDisplayPrice } from '../utils/cartItemPriceSelection'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import { useTripChatCheckoutActions } from '../hooks/useTripChatCheckoutActions'
import { useTripListNoteUpdate, type UpdateTripListNoteParams } from '../hooks/useTripListNoteUpdate'
import { buildItemNoteUpdatedActivityEntry } from '../utils/buildItemNoteUpdatedActivityEntry'
import { englishTypeLabel } from '../components/ListView/tripListTypeLabels'
import { rememberTripChatCheckoutReturnView } from '../hooks/useRightPanelState'
import { isCartPanelItemCheckoutReady } from '../utils/cartPanelUtils'
import {
  buildTripListRows,
  filterTripListRows,
  groupTripListRows,
  sortTripListRows,
} from '../components/ListView/tripListRows'
import {
  applyCartPanelPricesToTripListRows,
  getTripListRowTripItemId,
  isTripListRowMatchingCartPanelItem,
} from '../components/ListView/tripListCartPrices'
import { canMoveTripListRowToAnotherDay, type TripListMoveDayOption } from '../components/ListView/tripListMoveDayOptions'
import {
  getSelectedTripListRows,
  getTripListRowAskAiContext,
  getTripListRowsAskAiContext,
  getTripListRowsAskAiTitle,
  getTripListRowsMoveAskAiContext,
  getTripListRowsMoveAskAiPlaceholder,
  getTripListRowsMoveAskAiTitle,
  getTripListRowsTripItemIds,
} from '../components/ListView/tripListSelection'
import type {
  TripListFilters,
  TripListRow,
  TripListTransportRow,
  TripListRowType,
  TripListSort,
  TripListSortField,
} from '../components/ListView/tripListTypes'
import TripListView from '../components/ListView/TripListView'
import ConfirmDeleteTripListItemsModal from '../components/ListView/ConfirmDeleteTripListItemsModal'
import FlightSearchDrawer, { hasCompleteFlightSearchDefaults } from '../components/FlightSearchDrawer/FlightSearchDrawer'
import { ExperienceListingDrawer } from '../components/ExperienceListingDrawer'
import { HotelListingDrawer, type DrawerHotel } from '../components/HotelListingDrawer'
import type { FlightSearchDrawerParams } from '../components/FlightSearchDrawer/flightSearchDrawerParams'
import { applyRecentFlightSearchDefaults } from '../components/FlightSearchDrawer/flightSearchDrawerParams'
import {
  getFlightSearchDrawerParamsForTransport,
  getStopFlightSearchDefaults,
  resolveStopFlightSearchAirportDefaults,
} from '../components/FlightSearchDrawer/stopFlightSearchDrawerParams'
import { isBookedFlightOption } from '../components/ShortlistedFlightsSection/flightShortlistUtils'
import useBookShortlistedFlight from '../components/ShortlistedFlightsSection/useBookShortlistedFlight'
import useRefreshShortlistedFlights from '../components/ShortlistedFlightsSection/useRefreshShortlistedFlights'
import useViewBookedShortlistedFlight from '../components/ShortlistedFlightsSection/useViewBookedShortlistedFlight'

interface Props {
  visible: boolean
  ViewComponent?: ComponentType<ComponentProps<typeof TripListView>>
}

const EMPTY_TRIP_STOPS: Array<TripStop> = []
const ROW_BOOK_READINESS_TIMEOUT_MS = 2000
const MY_ESCAPES_URL = '/account/my-escapes'
const MAX_BULK_DELETE_ITEM_IDS = 100
const MAX_BULK_MOVE_ITEM_IDS = 20

type StopBrowseDrawerState =
  | { kind: 'flight'; stop: TripStop; flightSearchParams: FlightSearchDrawerParams; existingFlights?: Array<TransportOption> }
  | { kind: 'hotel'; stop: TripStop }
  | { kind: 'experience'; stop: TripStop }

function createDefaultFilters(): TripListFilters {
  return {
    dates: new Set<string>(),
    types: new Set<TripListRowType>(),
    locations: new Set<string>(),
    bookableOnly: false,
  }
}

// A shared, frozen instance for seeding refs/state that start at "no filters" but are never
// mutated in place -- every real update replaces the whole object (see filtersAtPanelOpenRef's
// own comment below), so reusing this one avoids allocating a throwaway pair of Sets wherever a
// fresh createDefaultFilters() isn't actually needed.
const DEFAULT_FILTERS: TripListFilters = Object.freeze(createDefaultFilters())

function getNextSort(sort: TripListSort | undefined, field: TripListSortField): TripListSort | undefined {
  if (sort?.field !== field) {
    return { field, direction: 'asc' }
  }

  if (sort.direction === 'asc') {
    return { field, direction: 'desc' }
  }

  return undefined
}

function getRowConfirmationCode(row: TripListRow): string | undefined {
  if (row.kind === 'item') {
    const item = row.item
    if (!('confirmationCode' in item)) return undefined
    return typeof item.confirmationCode === 'string' && item.confirmationCode.length > 0 ? item.confirmationCode : undefined
  }

  const savedItemData = row.option.savedItemData
  if (isLeFlightSavedItemData(savedItemData) && savedItemData.orderId) return savedItemData.orderId
  const returnLegSavedItemData = row.option.returnLeg?.savedItemData
  if (isLeFlightSavedItemData(returnLegSavedItemData) && returnLegSavedItemData.orderId) return returnLegSavedItemData.orderId
  return undefined
}

function getBookingUrl(row: TripListRow): string {
  const confirmationCode = getRowConfirmationCode(row)
  return confirmationCode ? `${MY_ESCAPES_URL}/order/${confirmationCode}` : MY_ESCAPES_URL
}

const DELETED_ITEM_COUNT = defineMessage({
  id: 'im2RzOXxzGWUvRmd0Swz',
  defaultMessage: '{count, plural, one {# item} other {# items}}',
  description: 'Names how many itinerary items a bulk delete removed, shown under the trip planner snackbar heading',
})

function ListContainer({ visible, ViewComponent = TripListView }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const { currentRegionCode } = useContext(GeoContext)
  const currentCurrency = useContext(CurrencyContext)
  const eventBus = useTripPlannerEventBus()
  const { sendMessage } = useChatActions()
  const {
    viewBookedFlightDetails,
    viewUnbookedFlightDetails,
    isFlightDetailsDrawerOpen,
    closeFlightDetailsDrawer,
    flightDetailsDrawer,
  } = useViewBookedShortlistedFlight()
  const { bookFlight } = useBookShortlistedFlight(showModal)
  const { refresh: refreshShortlistedFlights } = useRefreshShortlistedFlights()
  const { chatId, isStreaming, isStreamOpen } = useChatStreamStatus()
  const { luxPlusEnabled, onLuxPlusToggle } = useTripListPricingContext()
  const {
    itineraryData,
    multiCartId,
    scrollTarget,
    clearScrollTarget,
    deleteManualItineraryTarget,
    moveManualItineraryItems,
    isManualItineraryDeletePending,
    isManualItineraryMovePending,
    isFlightCartMutationPending,
    reconcileCartForCheckout,
  } = useItineraryContext()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const actionsDisabled = isStreaming || isStreamOpen || isManualItineraryDeletePending || isManualItineraryMovePending || isFlightCartMutationPending

  const [sort, setSort] = useState<TripListSort | undefined>(undefined)
  const [filters, setFilters] = useState<TripListFilters>(createDefaultFilters)
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set())
  const [pendingBookRow, setPendingBookRow] = useState<TripListRow | undefined>(undefined)
  const [flightSearchRow, setFlightSearchRow] = useState<TripListTransportRow | undefined>(undefined)
  const [stopBrowseDrawer, setStopBrowseDrawer] = useState<StopBrowseDrawerState | undefined>(undefined)
  // Seeded from the shared DEFAULT_FILTERS reference, not a fresh createDefaultFilters() call --
  // useRef only reads its argument once but still evaluates it every render, so calling
  // createDefaultFilters() here (unlike the lazy useState initializer above) would allocate a
  // fresh filters object, two Sets included, on every render just to discard it. Safe to share:
  // nothing ever mutates a filters object's Sets in place, every update reassigns .current to a
  // brand new object (see the auto-prune effect, handleClearFilters, handleFilterPanelOpened
  // below), so momentarily pointing at the same frozen object as another instance's initial
  // render is never observable.
  const filtersAtPanelOpenRef = useRef<TripListFilters>(DEFAULT_FILTERS)

  // Shared by every path that can change `filters` outside a single onXFiltersChange call --
  // panel close, "Reset all", and the auto-prune effect below -- so all three describe what
  // actually changed (including a real change down to nothing, which still gets a "cleared" entry
  // rather than going silent; see buildListFilterChangedActivityEntry) instead of only the panel
  // path being instrumented.
  const dispatchFilterChanges = useCallback((previous: TripListFilters, next: TripListFilters) => {
    const previousBookableOnlySuffix = previous.bookableOnly ? '(bookable only)' : undefined
    const nextBookableOnlySuffix = next.bookableOnly ? '(bookable only)' : undefined
    const entries = [
      buildListFilterChangedActivityEntry('date', [...previous.dates], [...next.dates]),
      buildListFilterChangedActivityEntry('location', [...previous.locations], [...next.locations]),
      buildListFilterChangedActivityEntry(
        'type',
        [...previous.types].map(englishTypeLabel),
        [...next.types].map(englishTypeLabel),
        nextBookableOnlySuffix,
        previousBookableOnlySuffix,
      ),
    ].filter((entry): entry is NonNullable<typeof entry> => !!entry)
    if (entries.length > 0) {
      eventBus.dispatch({ type: 'trip_planner_surface_activity', entries })
    }
  }, [eventBus])

  const bookRowRequestLockRef = useRef(false)
  const stopFlightSearchRequestRef = useRef(0)
  const flightDetailsRequestRef = useRef(0)
  const visibleItineraryData = visible ? itineraryData : undefined
  const hasDomainDrawerOpen = !!flightSearchRow || !!stopBrowseDrawer || isFlightDetailsDrawerOpen
  const previousHasDomainDrawerOpenRef = useRef(hasDomainDrawerOpen)

  useFetchCartPanelOffers(visibleItineraryData, visible)
  // Shared canonical cart data; only the visible List contributes preview prices.
  const {
    items: cartPanelItems,
    isLoading: cartPanelItemsLoading,
    refetchCartItems,
  } = useCartProjection()
  const cartItemPrices = useMemo(() => cartPanelItems.map(item => {
    const price = getCartItemDisplayPrice(item.pricing, luxPlusEnabled)
    return {
      itemId: String(item.tripItemId),
      displayedPrice: price === undefined ? undefined : formatCurrency(price, currentCurrency, currentRegionCode),
    }
  }), [cartPanelItems, luxPlusEnabled, currentCurrency, currentRegionCode])
  const { checkoutCartItems } = useTripChatCheckoutActions({ chatId, multiCartId, refetchCartItems, reconcileCartForCheckout })
  const handleNoteUpdated = useCallback((params: UpdateTripListNoteParams) => {
    if (!params.title || !params.occurredAt) return
    eventBus.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildItemNoteUpdatedActivityEntry(params.title, params.occurredAt)],
    })
  }, [eventBus])

  const { isUpdating: isNoteUpdating, pendingRowId: noteUpdateRowId, updateNote } = useTripListNoteUpdate(chatId, currentRegionCode, handleNoteUpdated)
  const handleBookingUpdated = useCallback((params: UpdateTripListBookingParams) => {
    eventBus.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildItemBookingChangedActivityEntry(params.title, params.isBooked, params.occurredAt)],
    })
  }, [eventBus])
  const { mutate: updateBooking, isPending: isBookingUpdating } = useTripListBookingUpdate(chatId, currentRegionCode, handleBookingUpdated)
  const cartPanelItemsLoadingRef = useRef(cartPanelItemsLoading)

  useEffect(() => {
    cartPanelItemsLoadingRef.current = cartPanelItemsLoading
  }, [cartPanelItemsLoading])

  useEffect(() => {
    if (previousHasDomainDrawerOpenRef.current === hasDomainDrawerOpen) return

    previousHasDomainDrawerOpenRef.current = hasDomainDrawerOpen
    eventBus.dispatch({
      type: 'right_panel_domain_drawer_state_changed',
      isOpen: hasDomainDrawerOpen,
    })
  }, [eventBus, hasDomainDrawerOpen])

  useEffect(() => () => {
    if (!previousHasDomainDrawerOpenRef.current) return

    eventBus.dispatch({
      type: 'right_panel_domain_drawer_state_changed',
      isOpen: false,
    })
  }, [eventBus])

  useEventSubscription('close_right_panel_domain_drawers', () => {
    setFlightSearchRow(undefined)
    setStopBrowseDrawer(undefined)
    closeFlightDetailsDrawer()
  })

  const baseRows = useMemo(() => buildTripListRows(itineraryData, intl), [intl, itineraryData])
  const allRows = useMemo(
    () => applyCartPanelPricesToTripListRows(baseRows, cartPanelItems, currentCurrency),
    [baseRows, cartPanelItems, currentCurrency],
  )

  // Reads `filters` directly (now a dependency), not the setFilters updater's `prev` -- assigning
  // inside the updater and reading the assignment on the next statement relied on React's eager-
  // state fast path, which only runs an updater synchronously when the fiber has no other update
  // already queued; when it doesn't (e.g. another subscription's setState landing in the same
  // batch), the updater is deferred to the render phase and the read below sees nothing, silently
  // skipping the dispatch while the prune still happens. Reading `filters` from scope has no such
  // gap. The early-bail below keeps this idempotent despite `filters` now being a dependency (this
  // effect re-running on every filter tick, not just an allRows change, is harmless -- it just
  // recomputes and finds nothing to prune).
  //
  // useIsomorphicLayoutEffect, not useEffect -- TripListView's useCloseDropdownWhenUnavailable
  // reacts to this same allRows change (a facet's hasXFilterOptions can flip false off the same
  // row data), and also dispatches via onFilterPanelClosed. React flushes every layout effect in
  // the tree, and any state updates they trigger, to a fixed point before any passive effect runs
  // -- so this always resolves (including the setFilters/ref-resync below) before that child
  // effect can observe `filters`. As a plain useEffect, ordering is child-before-parent for the
  // *same* commit, so the child would dispatch first using the not-yet-pruned `filters`, then this
  // effect would dispatch again with the corrected value -- two dispatches (one stale) for a
  // single real change, instead of one correct one.
  useIsomorphicLayoutEffect(() => {
    const availableDates = new Set(
      allRows
        .map(row => row.day?.date)
        .filter((date): date is string => date !== undefined),
    )
    const dates = new Set([...filters.dates].filter(date => availableDates.has(date)))
    const availableLocations = new Set(
      allRows
        .filter(row => row.kind === 'item')
        .map(row => row.stopLocation.filterValue),
    )
    const locations = new Set([...filters.locations].filter(location => availableLocations.has(location)))
    if (
      dates.size === filters.dates.size &&
      locations.size === filters.locations.size
    ) return

    const pruned = { ...filters, dates, locations }
    // Resyncs the snapshot too -- without this, a dropdown left open through this prune would have
    // its later close diff against the pre-prune snapshot and re-report the same change a second
    // time (the no-op gate can't catch it: the stale snapshot genuinely differs from the
    // now-pruned filters).
    filtersAtPanelOpenRef.current = pruned
    dispatchFilterChanges(filters, pruned)
    setFilters(pruned)
  }, [allRows, filters, dispatchFilterChanges])

  const visibleRows = useMemo(() => {
    return sortTripListRows(filterTripListRows(allRows, filters), sort, luxPlusEnabled)
  }, [allRows, filters, luxPlusEnabled, sort])
  const selectedRows = useMemo(
    () => getSelectedTripListRows(visibleRows, selectedRowIds),
    [selectedRowIds, visibleRows],
  )
  const hasActiveFilters = (
    filters.dates.size > 0 ||
    filters.locations.size > 0 ||
    filters.types.size > 0 ||
    filters.bookableOnly
  )
  const groups = useMemo(() => {
    return groupTripListRows(visibleRows, {
      sort,
      grouped: !sort,
      stops: itineraryData?.stops,
      showEmptyStops: !hasActiveFilters,
    }, intl)
  }, [hasActiveFilters, intl, itineraryData?.stops, sort, visibleRows])

  useEffect(() => {
    setSelectedRowIds(prev => {
      const visibleIds = new Set(visibleRows.map(row => row.id))
      const next = new Set([...prev].filter(id => visibleIds.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [visibleRows])

  useEffect(() => {
    if (!pendingBookRow) return
    if (!visible || !visibleRows.some(row => row.id === pendingBookRow.id)) {
      setPendingBookRow(undefined)
      bookRowRequestLockRef.current = false
    }
  }, [pendingBookRow, visible, visibleRows])

  const handleSelectAll = useCallback((checked: boolean) => {
    setSelectedRowIds(checked ? new Set(visibleRows.map(row => row.id)) : new Set())
  }, [visibleRows])

  const handleSelectRow = useCallback((rowId: string, checked: boolean) => {
    setSelectedRowIds(prev => {
      const next = new Set(prev)
      if (checked) {
        next.add(rowId)
      } else {
        next.delete(rowId)
      }
      return next
    })
  }, [])

  const handleClearSelection = useCallback(() => {
    setSelectedRowIds(new Set())
  }, [])

  // These three only ever update filter state now -- the activity dispatch for a filter change
  // lives in handleFilterPanelClosed below, firing once when the panel closes rather than once per
  // checkbox toggle inside it (a multi-select filter is one customer decision, not one per click).
  const handleDateFiltersChange = useCallback((dates: Set<string>) => {
    setFilters(prev => {
      return { ...prev, dates: new Set(dates) }
    })
  }, [])

  const handleLocationFiltersChange = useCallback((locations: Set<string>) => {
    setFilters(prev => {
      return { ...prev, locations: new Set(locations) }
    })
  }, [])

  const handleTypeFiltersChange = useCallback((types: Set<TripListRowType>, bookableOnly: boolean) => {
    setFilters(prev => {
      return { ...prev, types: new Set(types), bookableOnly }
    })
  }, [])

  // Snapshots the filter state at the moment a filter panel opens (desktop: one of three
  // independent dropdowns; mobile: the one shared filters modal, for any of its three sections) --
  // handleFilterPanelClosed below diffs against this snapshot rather than the previous render's
  // filters, so it reports what changed across the whole time the panel was open, not just the
  // last toggle before close.
  const handleFilterPanelOpened = useCallback(() => {
    filtersAtPanelOpenRef.current = filters
  }, [filters])

  const handleFilterPanelClosed = useCallback(() => {
    dispatchFilterChanges(filtersAtPanelOpenRef.current, filters)
  }, [filters, dispatchFilterChanges])

  const handleSortToggle = useCallback((field: TripListSortField) => {
    setSort(prev => getNextSort(prev, field))
  }, [])

  const handleClearFilters = useCallback(() => {
    const defaults = createDefaultFilters()
    dispatchFilterChanges(filters, defaults)
    // Resyncs the snapshot too -- on mobile, "Reset all" lives inside the still-open filters modal
    // and doesn't close it, so without this a later real close would diff the stale pre-clear
    // snapshot against the (already-default) filters and re-report the identical "cleared" change
    // a second time.
    filtersAtPanelOpenRef.current = defaults
    setFilters(defaults)
    setSort(undefined)
  }, [filters, dispatchFilterChanges])

  const openCartPanel = useCallback(() => {
    eventBus.dispatch({ type: 'open_cart_panel' })
  }, [eventBus])

  const closeFlightSearchDrawer = useCallback(() => {
    setFlightSearchRow(undefined)
  }, [])

  const flightSearchDrawerParams = useMemo(() => {
    if (!flightSearchRow) return undefined

    const stops = itineraryData?.stops ?? EMPTY_TRIP_STOPS
    const params = getFlightSearchDrawerParamsForTransport(
      stops,
      flightSearchRow.option,
      flightSearchRow.transport,
    )
    const stopId = flightSearchRow.transport.arrivalStopId ?? null

    return applyRecentFlightSearchDefaults(params, itineraryData, stopId, {
      preferParamsAsSelected: true,
    })
  }, [flightSearchRow, itineraryData])

  const openFlightSearchForRow = useCallback((row: TripListTransportRow) => {
    setStopBrowseDrawer(undefined)
    setFlightSearchRow(row)
  }, [])

  const handleOpenStopHotels = useCallback((stop: TripStop) => {
    setFlightSearchRow(undefined)
    setStopBrowseDrawer({ kind: 'hotel', stop })
  }, [])

  const handleOpenStopFlights = useCallback(async(stop: TripStop) => {
    setFlightSearchRow(undefined)
    setStopBrowseDrawer(undefined)

    const requestId = stopFlightSearchRequestRef.current + 1
    stopFlightSearchRequestRef.current = requestId
    const stops = itineraryData?.stops ?? EMPTY_TRIP_STOPS
    const flightSearchDefaults = getStopFlightSearchDefaults(stops, stop)
    const flightSearchParams = applyRecentFlightSearchDefaults(
      flightSearchDefaults.flightSearchParams,
      itineraryData,
      stop.id,
    )
    if (
      flightSearchParams.originAirportCode &&
      flightSearchParams.destinationAirportCode
    ) {
      setStopBrowseDrawer({
        kind: 'flight',
        stop,
        flightSearchParams,
        existingFlights: flightSearchDefaults.existingFlights,
      })
      return
    }
    const resolvedFlightSearchParams = await resolveStopFlightSearchAirportDefaults(
      stops,
      stop,
      flightSearchParams,
    )
    if (requestId !== stopFlightSearchRequestRef.current) return

    setStopBrowseDrawer({
      kind: 'flight',
      stop,
      flightSearchParams: resolvedFlightSearchParams,
      existingFlights: flightSearchDefaults.existingFlights,
    })
  }, [itineraryData])

  const handleOpenStopExperiences = useCallback((stop: TripStop) => {
    setFlightSearchRow(undefined)
    setStopBrowseDrawer({ kind: 'experience', stop })
    if (!opensOnThingsToDo({ stopCoordinates: getStopCoordinates(stop) })) return
    eventBus.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildThingsToDoOpenedActivityEntry(stop.placeName, 'trip_planner.list.stop_actions')],
    })
  }, [eventBus])

  const handleCloseStopBrowseDrawer = useCallback(() => {
    stopFlightSearchRequestRef.current += 1
    setStopBrowseDrawer(undefined)
  }, [])

  const handleStopHotelShowMap = useCallback((hotel: DrawerHotel) => {
    if (!hotel.geoLocation) return
    setStopBrowseDrawer(undefined)
    eventBus.dispatch({
      type: 'show_offer_suggestion_on_map',
      data: {
        offerId: hotel.id,
        name: hotel.name,
        coordinates: hotel.geoLocation,
        offerType: 'hotel',
      },
    })
  }, [eventBus])

  const handleStopExperienceShowMap = useCallback((experience: { offerId: string; name: string; geoLocation: { lat: number; lng: number } }) => {
    setStopBrowseDrawer(undefined)
    eventBus.dispatch({
      type: 'show_offer_suggestion_on_map',
      data: {
        offerId: experience.offerId,
        name: experience.name,
        coordinates: experience.geoLocation,
        offerType: 'experience',
      },
    })
  }, [eventBus])

  const handleUnbookedFlightDetailsClick = useCallback(async(row: TripListTransportRow) => {
    const requestId = flightDetailsRequestRef.current + 1
    flightDetailsRequestRef.current = requestId
    const refreshedFlights = await refreshShortlistedFlights([row.option])
    if (requestId !== flightDetailsRequestRef.current) return

    const refreshedData = refreshedFlights.find(flight => flight.option.itemId === row.option.itemId)

    const didOpenDetails = await viewUnbookedFlightDetails(
      row.option,
      refreshedData,
      bookFlight,
      undefined,
      { suppressUnavailableMessage: true },
    )
    if (requestId !== flightDetailsRequestRef.current) return

    if (!didOpenDetails) {
      openFlightSearchForRow(row)
    }
  }, [bookFlight, openFlightSearchForRow, refreshShortlistedFlights, viewUnbookedFlightDetails])

  const handleRowClick = useCallback((row: TripListRow) => {
    if (row.viewTarget.kind === 'none') return

    if (row.viewTarget.kind === 'offer') {
      eventBus.dispatch({
        type: 'open_offer_drawer',
        offerId: row.viewTarget.offerId,
        accommodationPropertyId: row.viewTarget.accommodationPropertyId,
        offerType: row.viewTarget.offerType,
        searchParams: row.viewTarget.searchParams,
      })
      return
    }

    if (row.viewTarget.kind === 'place') {
      eventBus.dispatch({
        type: 'open_place_drawer',
        googlePlaceId: row.viewTarget.googlePlaceId,
        placeName: row.viewTarget.placeName,
      })
      eventBus.dispatch({
        type: 'trip_planner_surface_activity',
        entries: [buildPlaceDrawerOpenedActivityEntry(row.viewTarget.placeName, 'trip_planner.list.row', row.viewTarget.googlePlaceId)],
      })
      return
    }

    if (row.viewTarget.kind === 'flightSearch') {
      if (row.kind === 'transport') {
        openFlightSearchForRow(row)
      }
      return
    }

    if (row.viewTarget.kind === 'flightDetails') {
      if (row.kind === 'transport') {
        if (isBookedFlightOption(row.option)) {
          viewBookedFlightDetails(row.option)
        } else {
          handleUnbookedFlightDetailsClick(row).catch(() => openFlightSearchForRow(row))
        }
      }
      return
    }

    eventBus.dispatch({
      type: 'open_place_search_drawer',
      searchQuery: row.viewTarget.searchQuery,
      placeName: row.viewTarget.placeName,
    })
  }, [eventBus, handleUnbookedFlightDetailsClick, openFlightSearchForRow, viewBookedFlightDetails])

  const routeToCompletion = useCallback((row: TripListRow) => {
    if (row.kind === 'transport') {
      openCartPanel()
      return
    }

    if (row.offerTarget) {
      eventBus.dispatch({
        type: 'open_offer_drawer',
        offerId: row.offerTarget.offerId,
        accommodationPropertyId: row.offerTarget.accommodationPropertyId,
        offerType: row.offerTarget.offerType,
        searchParams: row.offerTarget.searchParams,
        scrollToPricing: row.rowType === 'experience',
        scrollToRoomOptions: row.rowType === 'accommodation',
      })
      return
    }

    handleRowClick(row)
  }, [eventBus, handleRowClick, openCartPanel])

  const handleResolvedBookRow = useCallback((row: TripListRow) => {
    const matchedItem = cartPanelItems.find(item => isTripListRowMatchingCartPanelItem(row, item))
    if (!matchedItem) {
      routeToCompletion(row)
      return
    }

    if (isCartPanelItemCheckoutReady(matchedItem)) {
      checkoutCartItems([matchedItem])
      return
    }

    routeToCompletion(row)
  }, [cartPanelItems, checkoutCartItems, routeToCompletion])

  const handleBookRow = useCallback(async(row: TripListRow) => {
    if (actionsDisabled || bookRowRequestLockRef.current || pendingBookRow) return
    bookRowRequestLockRef.current = true
    rememberTripChatCheckoutReturnView('list')
    try {
      if (!isLoggedIn) {
        const loginResult = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
        if (!loginResult.loggedIn) {
          bookRowRequestLockRef.current = false
          return
        }
        setPendingBookRow(row)
        return
      }

      if (cartPanelItemsLoading) {
        setPendingBookRow(row)
        return
      }

      handleResolvedBookRow(row)
      bookRowRequestLockRef.current = false
    } catch(error) {
      bookRowRequestLockRef.current = false
      throw error
    }
  }, [actionsDisabled, cartPanelItemsLoading, handleResolvedBookRow, isLoggedIn, pendingBookRow, showModal])

  useEffect(() => {
    if (!pendingBookRow || !isLoggedIn || cartPanelItemsLoading) return
    const row = pendingBookRow
    setPendingBookRow(undefined)
    handleResolvedBookRow(row)
    bookRowRequestLockRef.current = false
  }, [cartPanelItemsLoading, handleResolvedBookRow, isLoggedIn, pendingBookRow])

  useEffect(() => {
    if (!pendingBookRow || !isLoggedIn || !cartPanelItemsLoading) return
    const timeout = window.setTimeout(() => {
      if (!cartPanelItemsLoadingRef.current) return
      routeToCompletion(pendingBookRow)
      setPendingBookRow(undefined)
      bookRowRequestLockRef.current = false
    }, ROW_BOOK_READINESS_TIMEOUT_MS)
    return () => window.clearTimeout(timeout)
  }, [cartPanelItemsLoading, isLoggedIn, pendingBookRow, routeToCompletion])

  const handleBookAll = useCallback(() => {
    if (actionsDisabled) return
    openCartPanel()
  }, [actionsDisabled, openCartPanel])

  const handleViewBooking = useCallback((row: TripListRow) => {
    if (actionsDisabled) return
    window.open(getBookingUrl(row), '_blank', 'noopener,noreferrer')
  }, [actionsDisabled])

  const handleAskAi = useCallback((row: TripListRow, origin?: { x: number; y: number }) => {
    eventBus.dispatch({
      type: 'ask_ai_for_list_row',
      title: row.title,
      context: getTripListRowAskAiContext(row),
      origin,
    })
  }, [eventBus])

  const handleAskAiMove = useCallback((row: TripListRow, origin?: { x: number; y: number }) => {
    const rows = [row]

    eventBus.dispatch({
      type: 'ask_ai_for_list_row',
      title: getTripListRowsMoveAskAiTitle(intl, rows),
      context: getTripListRowsMoveAskAiContext(rows),
      placeholder: getTripListRowsMoveAskAiPlaceholder(intl, rows),
      origin,
    })
  }, [eventBus, intl])

  const handleAskAiSelectedRows = useCallback((origin?: { x: number; y: number }) => {
    if (actionsDisabled || selectedRows.length === 0) return
    if (selectedRows.length === 1 && selectedRows[0]) {
      handleAskAi(selectedRows[0], origin)
      return
    }

    eventBus.dispatch({
      type: 'ask_ai_for_list_row',
      title: getTripListRowsAskAiTitle(intl, selectedRows),
      context: getTripListRowsAskAiContext(selectedRows),
      origin,
    })
  }, [actionsDisabled, eventBus, handleAskAi, intl, selectedRows])

  const handleDelete = useCallback(async(row: TripListRow) => {
    if (actionsDisabled) return
    const itemId = row.kind === 'transport' ? row.option.itemId : row.item.id
    // Captured before the delete request round-trips below, not after.
    const occurredAt = new Date().toISOString()
    const deleted = await deleteManualItineraryTarget({ targetType: 'item', itemId }, row.title)
    if (deleted) {
      eventBus.dispatch({
        type: 'trip_planner_surface_activity',
        entries: [buildItemDeletedActivityEntry(row.title, 'trip_planner.list.delete_row', occurredAt)],
      })
    }
  }, [actionsDisabled, deleteManualItineraryTarget, eventBus])

  const handleMoveToDay = useCallback(async(row: TripListRow, option: TripListMoveDayOption) => {
    if (actionsDisabled || option.selected || !canMoveTripListRowToAnotherDay(row)) return false

    return moveManualItineraryItems({
      moves: [{
        itemId: getTripListRowTripItemId(row),
        target: option.target,
        placement: option.placement,
      }],
    })
  }, [actionsDisabled, moveManualItineraryItems])

  const handleAskAiMoveSelectedRows = useCallback((origin?: { x: number; y: number }) => {
    if (actionsDisabled || selectedRows.length === 0) return
    if (selectedRows.length === 1 && selectedRows[0]) {
      handleAskAiMove(selectedRows[0], origin)
      return
    }

    eventBus.dispatch({
      type: 'ask_ai_for_list_row',
      title: getTripListRowsMoveAskAiTitle(intl, selectedRows),
      context: getTripListRowsMoveAskAiContext(selectedRows),
      placeholder: getTripListRowsMoveAskAiPlaceholder(intl, selectedRows),
      origin,
    })
  }, [actionsDisabled, eventBus, handleAskAiMove, intl, selectedRows])

  const handleMoveSelectedRowsToDay = useCallback(async(option: TripListMoveDayOption) => {
    if (actionsDisabled || option.selected || selectedRows.length === 0) return
    if (!selectedRows.every(canMoveTripListRowToAnotherDay)) return
    if (selectedRows.length > MAX_BULK_MOVE_ITEM_IDS) {
      showSnackbar(intl.formatMessage(
        {
          id: 'nloZuOC-U8iFPBm5spZ0',
          defaultMessage: 'Select {maxItemCount} or fewer items to move at once.',
          description: 'Error snackbar when too many trip planner list rows are selected for bulk moving',
        },
        { maxItemCount: MAX_BULK_MOVE_ITEM_IDS },
      ), 'critical')
      return
    }

    const moved = await moveManualItineraryItems({
      moves: selectedRows.map(row => ({
        itemId: getTripListRowTripItemId(row),
        target: option.target,
      })),
    })
    if (moved) setSelectedRowIds(new Set())
  }, [actionsDisabled, intl, moveManualItineraryItems, selectedRows])

  const handleDeleteSelectedRows = useCallback(async() => {
    if (actionsDisabled || selectedRows.length === 0) return

    const itemIds = getTripListRowsTripItemIds(selectedRows)
    if (itemIds.length > MAX_BULK_DELETE_ITEM_IDS) {
      showSnackbar(intl.formatMessage({
        id: 'NqT82TiGLwfahEZ0ENCm',
        defaultMessage: 'Select 100 or fewer items to delete at once.',
        description: 'Error snackbar when too many trip planner list rows are selected for bulk deletion',
      }), 'critical')
      return
    }

    const itemNames = selectedRows.map(row => row.title)
    const confirmed = await showModal<boolean>(<ConfirmDeleteTripListItemsModal itemNames={itemNames} />)
    if (!confirmed) return

    // Captured once the customer confirms, not after the delete request round-trips below.
    const occurredAt = new Date().toISOString()
    const deleted = await deleteManualItineraryTarget(
      { targetType: 'items', itemIds },
      itemNames.length === 1 ? itemNames[0] : intl.formatMessage(DELETED_ITEM_COUNT, { count: itemNames.length }),
    )
    if (deleted) {
      // Deduped by the same underlying trip-item id as itemIds (getTripListRowsTripItemIds already
      // dedupes there), not by name -- two selected rows can resolve to the same id (e.g. both legs
      // of a return flight), and deduping the names by string instead would risk merging two
      // genuinely different items that happen to share a title. itemNames above is left as-is for
      // the confirmation modal, which is meant to reflect every selected row back to the customer.
      //
      // First occurrence wins, not last -- `new Map(...)` keeps whichever row is last for a
      // duplicate key, but getTripListRowsTripItemIds's own `unique()` (new Set(array)) keeps the
      // first, so itemIds is keyed off the first-selected row of a duplicate pair. Building this
      // map any other way reports the entry under a different leg's name than the one the sent
      // itemIds actually resolve to.
      const nameByItemId = new Map<number, string>()
      for (const row of selectedRows) {
        const tripItemId = getTripListRowTripItemId(row)
        if (!nameByItemId.has(tripItemId)) nameByItemId.set(tripItemId, row.title)
      }
      eventBus.dispatch({
        type: 'trip_planner_surface_activity',
        entries: [buildItemsBulkDeletedActivityEntry([...nameByItemId.values()], 'trip_planner.list.bulk_delete', occurredAt)],
      })
    }
  }, [actionsDisabled, deleteManualItineraryTarget, eventBus, intl, selectedRows, showModal])

  const handleBookingChange = useCallback((row: TripListRow, isBooked: boolean) => {
    if (actionsDisabled || isNoteUpdating || isBookingUpdating || row.isLuxuryEscapesBooking) return
    updateBooking({ itemId: getTripListRowTripItemId(row), isBooked, title: row.title, occurredAt: new Date().toISOString() })
  }, [actionsDisabled, isNoteUpdating, isBookingUpdating, updateBooking])

  const handleUpdateNote = useCallback((row: TripListRow, notes: string | undefined) => {
    if (actionsDisabled || isNoteUpdating || isBookingUpdating) return
    updateNote({
      rowId: row.id,
      itemId: getTripListRowTripItemId(row),
      notes,
      title: row.title,
      // Captured now, not in onSuccess once the update request round-trips.
      occurredAt: new Date().toISOString(),
    })
  }, [actionsDisabled, isNoteUpdating, isBookingUpdating, updateNote])

  return (
    <>
      <CartDisplaySnapshotRegistration cartItemPrices={cartItemPrices} visible={visible} />
      {visible && (
        <>
          <ViewComponent
            groups={groups}
            tripStops={itineraryData?.stops ?? EMPTY_TRIP_STOPS}
            allRows={allRows}
            visibleRows={visibleRows}
            selectedRows={selectedRows}
            selectedRowIds={selectedRowIds}
            filters={filters}
            sort={sort}
            luxPlusEnabled={luxPlusEnabled}
            onSelectAll={handleSelectAll}
            onSelectRow={handleSelectRow}
            onDateFiltersChange={handleDateFiltersChange}
            onLocationFiltersChange={handleLocationFiltersChange}
            onTypeFiltersChange={handleTypeFiltersChange}
            onFilterPanelOpened={handleFilterPanelOpened}
            onFilterPanelClosed={handleFilterPanelClosed}
            onClearFilters={handleClearFilters}
            onSortToggle={handleSortToggle}
            onLuxPlusToggle={onLuxPlusToggle}
            onRowClick={handleRowClick}
            onBookRow={handleBookRow}
            onBookAll={handleBookAll}
            onViewBooking={handleViewBooking}
            onAskAi={handleAskAi}
            onAskAiMove={handleAskAiMove}
            onAskAiSelectedRows={handleAskAiSelectedRows}
            onAskAiMoveSelectedRows={handleAskAiMoveSelectedRows}
            onDelete={handleDelete}
            onMoveToDay={handleMoveToDay}
            onMoveSelectedRowsToDay={handleMoveSelectedRowsToDay}
            onDeleteSelectedRows={handleDeleteSelectedRows}
            onClearSelection={handleClearSelection}
            onOpenStopHotels={handleOpenStopHotels}
            onOpenStopFlights={handleOpenStopFlights}
            onOpenStopExperiences={handleOpenStopExperiences}
            onSendStopTimingEditMessage={sendMessage}
            onUpdateNote={handleUpdateNote}
            onBookingChange={handleBookingChange}
            bookingChangeDisabled={actionsDisabled || isNoteUpdating || isBookingUpdating}
            bookingRowId={pendingBookRow?.id}
            noteUpdateRowId={noteUpdateRowId}
            notesDisabled={actionsDisabled || isNoteUpdating || isBookingUpdating}
            actionsDisabled={actionsDisabled}
            scrollTarget={scrollTarget}
            onScrollComplete={clearScrollTarget}
          />
          {flightSearchRow && (
            <FlightSearchDrawer
              isOpen
              onClose={closeFlightSearchDrawer}
              originAirportCode={flightSearchDrawerParams?.originAirportCode}
              originAirportName={flightSearchDrawerParams?.originAirportName}
              destinationAirportCode={flightSearchDrawerParams?.destinationAirportCode}
              destinationAirportName={flightSearchDrawerParams?.destinationAirportName}
              departureDate={flightSearchDrawerParams?.departureDate}
              returnDate={flightSearchDrawerParams?.returnDate}
              indicativeSearchMonth={itineraryData?.indicativeSearchMonth}
              showMultiCity
              fareType={flightSearchDrawerParams?.fareType}
              fareClass={flightSearchDrawerParams?.fareClass}
              occupants={flightSearchDrawerParams?.occupants}
              autoSearch={hasCompleteFlightSearchDefaults({
                originAirportCode: flightSearchDrawerParams?.originAirportCode,
                destinationAirportCode: flightSearchDrawerParams?.destinationAirportCode,
                departureDate: flightSearchDrawerParams?.departureDate,
                returnDate: flightSearchDrawerParams?.returnDate,
                fareType: flightSearchDrawerParams?.fareType,
              })}
              onSendMessage={sendMessage}
              isStreaming={isStreaming}
              existingFlights={flightSearchRow.transport.options}
              tripStopId={flightSearchRow.transport.arrivalStopId ?? null}
            />
          )}
          {flightDetailsDrawer}
          {stopBrowseDrawer?.kind === 'flight' && (
            <FlightSearchDrawer
              isOpen
              onClose={handleCloseStopBrowseDrawer}
              originAirportCode={stopBrowseDrawer.flightSearchParams.originAirportCode}
              originAirportName={stopBrowseDrawer.flightSearchParams.originAirportName}
              destinationAirportCode={stopBrowseDrawer.flightSearchParams.destinationAirportCode}
              destinationAirportName={stopBrowseDrawer.flightSearchParams.destinationAirportName}
              departureDate={stopBrowseDrawer.flightSearchParams.departureDate}
              returnDate={stopBrowseDrawer.flightSearchParams.returnDate}
              indicativeSearchMonth={itineraryData?.indicativeSearchMonth}
              fareType={stopBrowseDrawer.flightSearchParams.fareType}
              fareClass={stopBrowseDrawer.flightSearchParams.fareClass}
              occupants={stopBrowseDrawer.flightSearchParams.occupants}
              showMultiCity
              onSendMessage={sendMessage}
              isStreaming={isStreaming}
              existingFlights={stopBrowseDrawer.existingFlights}
              tripStopId={stopBrowseDrawer.stop.id}
              autoSearch={hasCompleteFlightSearchDefaults({
                originAirportCode: stopBrowseDrawer.flightSearchParams.originAirportCode,
                destinationAirportCode: stopBrowseDrawer.flightSearchParams.destinationAirportCode,
                departureDate: stopBrowseDrawer.flightSearchParams.departureDate,
                returnDate: stopBrowseDrawer.flightSearchParams.returnDate,
                fareType: stopBrowseDrawer.flightSearchParams.fareType,
              })}
            />
          )}
          {stopBrowseDrawer?.kind === 'hotel' && (
            <HotelListingDrawer
              isOpen
              onClose={handleCloseStopBrowseDrawer}
              placeName={stopBrowseDrawer.stop.placeName}
              placeCountry={stopBrowseDrawer.stop.placeCountry}
              stopStartDate={stopBrowseDrawer.stop.startDate}
              stopEndDate={stopBrowseDrawer.stop.endDate}
              stopCoordinates={getStopCoordinates(stopBrowseDrawer.stop)}
              contextStop={stopBrowseDrawer.stop}
              onShowMap={handleStopHotelShowMap}
              itineraryData={itineraryData}
              onSendMessage={sendMessage}
            />
          )}
          {stopBrowseDrawer?.kind === 'experience' && (
            <ExperienceListingDrawer
              isOpen
              onClose={handleCloseStopBrowseDrawer}
              placeName={stopBrowseDrawer.stop.placeName}
              placeCountry={stopBrowseDrawer.stop.placeCountry}
              contextStop={stopBrowseDrawer.stop}
              stopCoordinates={getStopCoordinates(stopBrowseDrawer.stop)}
              itineraryData={itineraryData}
              onSendMessage={sendMessage}
              onShowExperienceOnMap={handleStopExperienceShowMap}
            />
          )}
        </>
      )}
    </>
  )
}

export default ListContainer
